import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Calendar, BarChart3 } from 'lucide-react';

const TradingChart: React.FC = () => {
  const [timeframe, setTimeframe] = useState('1D');
  
  // Mock gold price data
  const chartData = [
    { time: '09:00', price: 2030.50, volume: 1250 },
    { time: '10:00', price: 2032.75, volume: 1180 },
    { time: '11:00', price: 2028.90, volume: 1350 },
    { time: '12:00', price: 2031.20, volume: 1420 },
    { time: '13:00', price: 2034.50, volume: 1380 },
    { time: '14:00', price: 2036.80, volume: 1600 },
    { time: '15:00', price: 2035.25, volume: 1550 },
    { time: '16:00', price: 2038.90, volume: 1700 },
  ];

  const timeframes = ['1H', '4H', '1D', '1W', '1M'];

  return (
    <div className="win-chart-container">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-win-dark-text mb-1">Gold Price Chart</h2>
          <p className="text-win-dark-text-muted">Real-time XAU/USD price movement</p>
        </div>
        
        {/* Timeframe Selector */}
        <div className="flex items-center gap-2">
          <div className="flex bg-win-dark-surface-light rounded-3xl p-1">
            {timeframes.map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-4 py-2 rounded-3xl text-sm font-medium transition-all duration-200 ${
                  timeframe === tf
                    ? 'bg-win-dark-accent text-white shadow-md'
                    : 'text-win-dark-text-muted hover:text-win-dark-text'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Price Info */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-win-dark-surface-light rounded-3xl">
          <div className="flex items-center justify-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-sm text-win-dark-text-muted">Current Price</span>
          </div>
          <p className="text-2xl font-bold text-win-dark-text">$2,038.90</p>
          <p className="text-sm text-green-400 font-medium">+$8.40 (+0.41%)</p>
        </div>
        
        <div className="text-center p-4 bg-win-dark-surface-light rounded-3xl">
          <div className="flex items-center justify-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-win-dark-accent" />
            <span className="text-sm text-win-dark-text-muted">24h Volume</span>
          </div>
          <p className="text-2xl font-bold text-win-dark-text">125.4K</p>
          <p className="text-sm text-win-dark-text-muted">oz traded</p>
        </div>
        
        <div className="text-center p-4 bg-win-dark-surface-light rounded-3xl">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-win-dark-gold" />
            <span className="text-sm text-win-dark-text-muted">Market</span>
          </div>
          <p className="text-2xl font-bold text-green-400">OPEN</p>
          <p className="text-sm text-win-dark-text-muted">NY Session</p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#484848" />
            <XAxis 
              dataKey="time" 
              stroke="#9d9d9d"
              fontSize={12}
            />
            <YAxis 
              stroke="#9d9d9d"
              fontSize={12}
              domain={['dataMin - 5', 'dataMax + 5']}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#2d2d30',
                border: '1px solid #484848',
                borderRadius: '24px',
                color: '#ffffff',
                padding: '12px'
              }}
              formatter={(value: any, name: string) => [
                `$${value.toLocaleString()}`,
                name === 'price' ? 'Gold Price' : 'Volume'
              ]}
            />
            <Line 
              type="monotone" 
              dataKey="price" 
              stroke="#0078d4" 
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6, fill: '#0078d4', stroke: '#ffffff', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Chart Controls */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-win-dark-border">
        <div className="flex items-center gap-4">
          <button className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-3xl hover:bg-green-500/30 transition-colors">
            <TrendingUp className="w-4 h-4" />
            <span className="font-medium">BUY</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-3xl hover:bg-red-500/30 transition-colors">
            <TrendingUp className="w-4 h-4 rotate-180" />
            <span className="font-medium">SELL</span>
          </button>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-win-dark-text-muted">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>Live data • Updated 2s ago</span>
        </div>
      </div>
    </div>
  );
};

export default TradingChart;